import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';
import ItemsEditor from '../components/ItemsEditor';
import toast from 'react-hot-toast';

const today = () => new Date().toISOString().slice(0,10);
const inDays = (d) => new Date(Date.now()+d*86400000).toISOString().slice(0,10);

export default function QuotationForm() {
  const { id } = useParams(); const nav = useNavigate(); const isEdit = !!id;
  const [clients, setClients] = useState([]);
  const [items, setItems] = useState([{ item_name:'', description:'', quantity:1, unit_price:0, tax_percent:18 }]);
  const [form, setForm] = useState({ client_id:'', issue_date:today(), valid_until:inDays(30), status:'draft', discount:0, notes:'', terms:'Payment due within 30 days.', renewal_required:false, renewal_months:12 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/clients').then(r => setClients(r.data.data||[]));
    if (isEdit) {
      api.get(`/quotations/${id}`).then(r => {
        const q = r.data.data;
        setForm({ client_id:q.client_id, issue_date:q.issue_date?.slice(0,10)||today(), valid_until:q.valid_until?.slice(0,10)||inDays(30), status:q.status, discount:q.discount||0, notes:q.notes||'', terms:q.terms||'', renewal_required:!!parseInt(q.renewal_required), renewal_months:q.renewal_months||12 });
        setItems(q.items.map(i=>({ item_name:i.item_name, description:i.description||'', quantity:+i.quantity, unit_price:+i.unit_price, tax_percent:+i.tax_percent })));
      });
    }
  }, [id]);

  const sub  = items.reduce((s,i)=>s+i.quantity*i.unit_price,0);
  const tax  = items.reduce((s,i)=>s+i.quantity*i.unit_price*(i.tax_percent/100),0);
  const total= sub+tax-(parseFloat(form.discount)||0);

  const save = async (e) => {
    e.preventDefault();
    if (!form.client_id) { toast.error('Select a client'); return; }
    if (!items.length || items.some(i=>!i.item_name)) { toast.error('Fill all item names'); return; }
    setSaving(true);
    try {
      const payload = { ...form, renewal_required: form.renewal_required ? 1 : 0, items };
      if (isEdit) { await api.put(`/quotations/${id}`, payload); toast.success('Quotation updated!'); }
      else { await api.post('/quotations', payload); toast.success('Quotation created!'); }
      nav('/quotations');
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2 className="page-title">{isEdit ? 'Edit Quotation' : 'New Quotation'}</h2>
          <p className="page-subtitle">{isEdit ? 'Update quotation details' : 'Create a new quotation for your client'}</p>
        </div>
        <button className="btn btn-secondary" onClick={()=>nav('/quotations')}>← Back</button>
      </div>

      <form onSubmit={save}>
        <div className="grid-2" style={{ marginBottom:18 }}>
          {/* Left: details */}
          <div className="card">
            <div className="card-header"><span className="card-title">Quotation Details</span></div>
            <div className="form-group">
              <label className="form-label">Client *</label>
              <select className="form-control" value={form.client_id} onChange={e=>setForm({...form,client_id:e.target.value})} required>
                <option value="">— Select Client —</option>
                {clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Issue Date</label>
                <input type="date" className="form-control" value={form.issue_date} onChange={e=>setForm({...form,issue_date:e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Valid Until</label>
                <input type="date" className="form-control" value={form.valid_until} onChange={e=>setForm({...form,valid_until:e.target.value})} />
              </div>
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-control" value={form.status} onChange={e=>setForm({...form,status:e.target.value})}>
                  <option value="draft">Draft</option><option value="sent">Sent</option>
                  <option value="accepted">Accepted</option><option value="rejected">Rejected</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Discount (₹)</label>
                <input type="number" min="0" step=".01" className="form-control" value={form.discount} onChange={e=>setForm({...form,discount:e.target.value})} />
              </div>
            </div>

            {/* Renewal toggle */}
            <div style={{ padding:14,background:'var(--bg4)',borderRadius:'var(--r)',border:'1px solid var(--border)' }}>
              <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8 }}>
                <div>
                  <div style={{ fontSize:13,fontWeight:600,color:'var(--text)' }}>🔄 Requires Renewal?</div>
                  <div style={{ fontSize:11.5,color:'var(--text3)',marginTop:2 }}>Auto-creates a renewal entry when converted to invoice</div>
                </div>
                <label className="toggle">
                  <input type="checkbox" checked={form.renewal_required} onChange={e=>setForm({...form,renewal_required:e.target.checked})} />
                  <span className="toggle-slider" />
                </label>
              </div>
              {form.renewal_required && (
                <div className="form-group" style={{ marginBottom:0 }}>
                  <label className="form-label">Renewal after (months)</label>
                  <select className="form-control" value={form.renewal_months} onChange={e=>setForm({...form,renewal_months:parseInt(e.target.value)})}>
                    {[1,3,6,12,24,36].map(m=><option key={m} value={m}>{m} month{m>1?'s':''}</option>)}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Right: notes + total */}
          <div className="card">
            <div className="card-header"><span className="card-title">Notes & Summary</span></div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-control" rows={3} value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} placeholder="Additional notes for the client…" />
            </div>
            <div className="form-group">
              <label className="form-label">Terms & Conditions</label>
              <textarea className="form-control" rows={3} value={form.terms} onChange={e=>setForm({...form,terms:e.target.value})} />
            </div>
            <div style={{ background:'var(--bg4)',borderRadius:'var(--r)',padding:14 }}>
              {[['Subtotal',`₹${sub.toFixed(2)}`],['Tax',`₹${tax.toFixed(2)}`],['Discount',`- ₹${parseFloat(form.discount||0).toFixed(2)}`]].map(([l,v])=>(
                <div key={l} style={{ display:'flex',justifyContent:'space-between',fontSize:12.5,marginBottom:5,color:'var(--text2)' }}><span>{l}</span><span>{v}</span></div>
              ))}
              <div style={{ display:'flex',justifyContent:'space-between',fontSize:16,fontWeight:800,paddingTop:9,borderTop:'1px solid var(--border)',color:'var(--accent2)' }}>
                <span>Total</span><span>₹{total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="card" style={{ marginBottom:18 }}>
          <ItemsEditor items={items} setItems={setItems} />
        </div>

        <div style={{ display:'flex',justifyContent:'flex-end',gap:10 }}>
          <button type="button" className="btn btn-secondary" onClick={()=>nav('/quotations')}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? <span className="spinner"/> : (isEdit ? '✓ Update' : '✓ Create Quotation')}
          </button>
        </div>
      </form>
    </div>
  );
}
